import React from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';


import * as addressConstants from '../addressConstants';
import * as addressFormConstants from './addressFormConstants';

import {inputConstants} from '../../../global/Form/Input';

import FormBlock from '../../../global/Form/FormBlock';
import FormCountrySelect from '../../../global/Form/FormCountrySelect';
import FormTextInput from '../../../global/Form/FormElement/FormTextInput';
import ValidatingForm from '../../../global/Form/ValidatingForm';
import {I18nMessage} from '../../I18n';


function AddressForm({address, countryFilter, formId, hasName, legendI18n, onChange}) {

    const {formatMessage} = useIntl();

    const validationSchema = hasName
        ? addressFormConstants.SCHEMA_ADDRESS_WITH_NAME
        : addressFormConstants.SCHEMA_ADDRESS;

    return (
        <ValidatingForm
            data={address}
            id={formId}
            onIsInvalid={data => onChange(data, false)}
            onIsValid={data => onChange(data, true)}
            validationSchema={validationSchema}
        >
            {({form}) => (
                <FormBlock>

                    {legendI18n && (
                        <legend>
                            <I18nMessage id={legendI18n}/>
                        </legend>
                    )}

                    {hasName && (
                        <>
                            <FormTextInput
                                autocomplete={inputConstants.AUTOCOMPLETE_NAME}
                                error={form.getCurrentError('name')}
                                id={'name'}
                                isRequired={form.isRequired('name')}
                                labelI18n={'address.form.field.name.label'}
                                maxLength={addressFormConstants.NAME_MAX_LENGTH}
                                name={'name'}
                                onChange={name => form.setData({name})}
                                placeholder={formatMessage({id: 'address.form.field.name.placeholder'})}
                                value={form.getData('name')}
                            />
                            <FormTextInput
                                autocomplete={inputConstants.AUTOCOMPLETE_NAME_ADDITIONAL}
                                error={form.getCurrentError('nameAddition')}
                                id={'nameAddition'}
                                isRequired={form.isRequired('nameAddition')}
                                labelI18n={'address.form.field.nameAddition.label'}
                                maxLength={addressFormConstants.NAME_ADDITION_MAX_LENGTH}
                                name={'nameAddition'}
                                onChange={nameAddition => form.setData({nameAddition})}
                                placeholder={formatMessage({id: 'address.form.field.nameAddition.placeholder'})}
                                value={form.getData('nameAddition')}
                            />
                        </>
                    )}

                    <FormTextInput
                        autocomplete={inputConstants.AUTOCOMPLETE_ADDRESS_LINE_1}
                        error={form.getCurrentError('addressLine1')}
                        id={'addressLine1'}
                        isRequired={form.isRequired('addressLine1')}
                        labelI18n={'address.form.field.addressLine1.label'}
                        maxLength={addressFormConstants.ADDRESS_LINE_MAX_LENGTH}
                        name={'addressLine1'}
                        onChange={addressLine1 => form.setData({addressLine1})}
                        placeholder={formatMessage({id: 'address.form.field.addressLine1.placeholder'})}
                        value={form.getData('addressLine1')}
                    />

                    <FormTextInput
                        autocomplete={inputConstants.AUTOCOMPLETE_ADDRESS_LINE_2}
                        error={form.getCurrentError('addressLine2')}
                        id={'addressLine2'}
                        isRequired={form.isRequired('addressLine2')}
                        labelI18n={'address.form.field.addressLine2.label'}
                        maxLength={addressFormConstants.ADDRESS_LINE_MAX_LENGTH}
                        name={'addressLine2'}
                        onChange={addressLine2 => form.setData({addressLine2})}
                        placeholder={formatMessage({id: 'address.form.field.addressLine2.placeholder'})}
                        value={form.getData('addressLine2')}
                    />

                    <FormTextInput
                        autocomplete={inputConstants.AUTOCOMPLETE_POSTAL_CODE}
                        error={form.getCurrentError('postCode')}
                        id={'postCode'}
                        isRequired={form.isRequired('postCode')}
                        labelI18n={'address.form.field.postCode.label'}
                        maxLength={addressFormConstants.POST_CODE_MAX_LENGTH}
                        name={'postCode'}
                        onChange={postCode => form.setData({postCode})}
                        placeholder={formatMessage({id: 'address.form.field.postCode.placeholder'})}
                        value={form.getData('postCode')}
                    />

                    <FormTextInput
                        autocomplete={inputConstants.AUTOCOMPLETE_ADDRESS_LEVEL_2}
                        error={form.getCurrentError('city')}
                        id={'city'}
                        isRequired={form.isRequired('city')}
                        labelI18n={'address.form.field.city.label'}
                        maxLength={addressFormConstants.CITY_MAX_LENGTH}
                        name={'city'}
                        onChange={city => form.setData({city})}
                        placeholder={formatMessage({id: 'address.form.field.city.placeholder'})}
                        value={form.getData('city')}
                    />

                    <FormTextInput
                        autocomplete={inputConstants.AUTOCOMPLETE_ADDRESS_LEVEL_1}
                        error={form.getCurrentError('state')}
                        id={'state'}
                        isRequired={form.isRequired('state')}
                        labelI18n={'address.form.field.state.label'}
                        maxLength={addressFormConstants.STATE_MAX_LENGTH}
                        name={'state'}
                        onChange={state => form.setData({state})}
                        placeholder={formatMessage({id: 'address.form.field.state.placeholder'})}
                        value={form.getData('state')}
                    />

                    <FormCountrySelect
                        error={form.getCurrentError('countryAlpha2')}
                        filter={countryFilter}
                        id={'countryAlpha2'}
                        isRequired={form.isRequired('countryAlpha2')}
                        labelI18n={'address.form.field.countryAlpha2.label'}
                        name={'countryAlpha2'}
                        onChange={countryAlpha2 => form.setData({countryAlpha2})}
                        placeholder={formatMessage({id: 'address.form.field.countryAlpha2.placeholder'})}
                        value={form.getData('countryAlpha2')}
                    />

                </FormBlock>
            )}
        </ValidatingForm>
    );
}


AddressForm.propTypes = {
    address: addressConstants.SHAPE_ADDRESS,
    countryFilter: PropTypes.func,
    formId: PropTypes.string.isRequired,
    hasName: PropTypes.bool.isRequired,
    legendI18n: PropTypes.string,
    onChange: PropTypes.func.isRequired
};

AddressForm.defaultProps = {
    formId: 'address.form',
    hasName: false
};


export default AddressForm;
